/**
 * Frontend Zod schemas — mirror the data-api contract (which mirrors
 * `contracts/openapi.yaml` and `contracts/events/*.schema.json`).
 *
 * Keeping a copy here (rather than importing from data-api) lets the
 * frontend ship as a standalone build artifact.
 *
 * V2-405 contract drift: the test suite loads `contracts/events/examples/*.json`
 * and asserts each one parses cleanly here.
 */
import { z } from "zod";

export const decimalString = z.string().regex(/^-?\d+(\.\d+)?$/);
export const oddsString = z.string().regex(/^\d+(\.\d+)?$/);
export const isoTimestamp = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/);

export const Direction = z.enum(["UP", "DOWN"]);
export const Mode = z.enum(["live", "paper"]);
export const FeedHealth = z.enum(["ok", "stale", "degraded", "down"]);

export const ApiError = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiError>;

const Cursor = z.object({
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
});

// ----- WS events ------------------------------------------------------------

const Envelope = z.object({
  event_type: z.string().regex(/^live\.[a-z_]+$/),
  ts: isoTimestamp,
  slug: z.string().max(256),
  payload: z.unknown(),
  config_version_id: z.string().nullable().optional(),
  seq: z.number().int().nonnegative().optional(),
  /** V3 multi-market: optional (V2 events omit it; consumers default to btc-5m). */
  market_id: z.string().regex(/^[a-z][a-z0-9-]*$/).optional(),
});

const SideQuote = z.object({
  bid: oddsString.nullable(),
  ask: oddsString.nullable(),
  mid: oddsString.nullable().optional(),
});

export const PriceTickEvent = Envelope.extend({
  event_type: z.literal("live.price_tick"),
  payload: z.object({
    btc_price: decimalString,
    // V3 multi-market: secs_left ranges up to window_seconds for the
    // tick's market (5m=300, 15m=900, 1h=3600, 4h=14400). The old
    // max(600) was a V2 5m+grace cap.
    secs_left: z.number().int().nonnegative(),
    mode: Mode.optional(),
    feed_source: z.string(),
    feed_health: FeedHealth.optional(),
  }),
});
export const OddsTickEvent = Envelope.extend({
  event_type: z.literal("live.odds_tick"),
  payload: z.object({
    up: SideQuote, down: SideQuote, mode: Mode.optional(), feed_health: FeedHealth.optional(),
  }),
});
export const DecisionEvent = Envelope.extend({
  event_type: z.literal("live.decision"),
  config_version_id: z.string(),
  payload: z.object({
    outcome: z.enum(["place", "skip", "blocked", "monitor", "already_placed"]),
    direction: Direction.nullable().optional(),
    favorite_odds: oddsString.nullable().optional(),
    matching_candles: z.number().int().nonnegative().nullable().optional(),
    candles_evaluated: z.number().int().nonnegative().nullable().optional(),
    block_reason: z.string().nullable().optional(),
    checks: z.array(z.object({
      name: z.string(), passed: z.boolean(),
      value: z.unknown().optional(), threshold: z.unknown().optional(),
      detail: z.string().nullable().optional(),
      // Optional bot per-minute view forwarded by the candle_agreement
      // check. Older bot builds omit these and still validate.
      per_minute: z.array(z.object({
        minute: z.number().int(),
        open: z.string().nullable().optional(),
        close: z.string().nullable().optional(),
        delta: z.string(),
        dir: z.enum(["UP", "DOWN", "FLAT"]),
        samples: z.number().int().nonnegative(),
        is_live: z.boolean(),
        // Optional: bot builds prior to the per-minute expansion only
        // emitted the evaluated tail and omitted this flag. The
        // dashboard parser defaults it to ``true`` for those legacy
        // payloads.
        evaluated: z.boolean().optional(),
        matches: z.boolean(),
      })).nullable().optional(),
      direction_evaluated: z.enum(["UP", "DOWN"]).nullable().optional(),
    })),
  }),
});
export const OrderEventEvent = Envelope.extend({
  event_type: z.literal("live.order_event"),
  config_version_id: z.string(),
  payload: z.object({
    order_id: z.string(),
    exchange_order_id: z.string().nullable().optional(),
    phase: z.enum([
      "intent","submitted","accepted","partially_filled","filled",
      "rejected","expired","cancelled","sell_intent","sell_submitted",
      "sell_filled","failed","resolved",
    ]),
    direction: Direction,
    side: z.enum(["BUY", "SELL"]).optional(),
    price: oddsString.nullable().optional(),
    size_shares: decimalString.nullable().optional(),
    size_usdc: decimalString.nullable().optional(),
    filled_shares: decimalString.nullable().optional(),
    fee_usdc: decimalString.nullable().optional(),
    pnl_usdc: decimalString.nullable().optional(),
    is_paper: z.boolean().optional(),
    error: z.object({
      code: z.string(), message: z.string(), retryable: z.boolean().optional(),
    }).nullable().optional(),
  }),
});
export const BotStatusEvent = Envelope.extend({
  event_type: z.literal("live.bot_status"),
  payload: z.object({
    state: z.enum(["starting","running","paused","degraded","stopping","stopped"]),
    mode: Mode,
    feed_health: FeedHealth.optional(),
    active_config_version_id: z.string().nullable().optional(),
    active_window: z.object({
      slug: z.string(),
      window_start: isoTimestamp,
      window_end: isoTimestamp,
    }).nullable().optional(),
    unresolved_count: z.number().int().nonnegative().optional(),
    last_heartbeat_ts: isoTimestamp.optional(),
    version: z.string().optional(),
    message: z.string().nullable().optional(),
  }),
});
export const SimProgressEvent = Envelope.extend({
  event_type: z.literal("live.sim_progress"),
  payload: z.object({
    run_id: z.string(),
    mode: z.enum(["single", "sweep"]),
    status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
    progress: z.number().min(0).max(1),
    windows_done: z.number().int().nonnegative().nullable().optional(),
    windows_total: z.number().int().nonnegative().nullable().optional(),
    message: z.string().nullable().optional(),
  }),
});
export const LiveEvent = z.discriminatedUnion("event_type", [
  PriceTickEvent, OddsTickEvent, DecisionEvent, OrderEventEvent, BotStatusEvent, SimProgressEvent,
]);
export type LiveEvent = z.infer<typeof LiveEvent>;

// ----- REST DTOs ------------------------------------------------------------

export const Health = z.object({
  status: z.enum(["ok", "degraded", "down"]),
  version: z.string(),
  db: z.enum(["ok", "degraded", "down"]),
  ws_relay: z.enum(["ok", "degraded", "down"]),
  bot_last_seen_ts: isoTimestamp.nullable(),
});
export type Health = z.infer<typeof Health>;

// V3 multi-market catalogue ----------------------------------------------------

export const Market = z.object({
  market_id: z.string().regex(/^[a-z][a-z0-9-]*$/),
  asset: z.string(),
  window_seconds: z.number().int().positive(),
  bucket_seconds: z.number().int().positive(),
  display_name: z.string(),
  short_name: z.string(),
  slug_pattern: z.string(),
  realtime_topic: z.string(),
  realtime_symbol: z.string(),
  default_tick_size: z.string(),
  enabled: z.boolean(),
  color_hex: z.string().nullable(),
  notes: z.string().nullable(),
});
export type Market = z.infer<typeof Market>;

export const MarketList = z.object({ items: z.array(Market) });
export type MarketList = z.infer<typeof MarketList>;

export const LiveMarketsList = z.object({ items: z.array(z.string()) });
export type LiveMarketsList = z.infer<typeof LiveMarketsList>;

export const BotStatus = BotStatusEvent.shape.payload;
export type BotStatus = z.infer<typeof BotStatus>;

export const LiveWindow = z.object({
  slug: z.string(),
  window_start: isoTimestamp,
  window_end: isoTimestamp,
  secs_left: z.number().int().nonnegative(),
  btc_price: decimalString,
  odds: z.object({
    up: z.object({ bid: oddsString.nullable(), ask: oddsString.nullable() }),
    down: z.object({ bid: oddsString.nullable(), ask: oddsString.nullable() }),
  }),
  config_version_id: z.string(),
  active_order_id: z.string().nullable(),
});
export type LiveWindow = z.infer<typeof LiveWindow>;

export const WindowSummary = z.object({
  slug: z.string(),
  window_start: isoTimestamp,
  window_end: isoTimestamp,
  status: z.enum(["open", "awaiting_resolution", "resolved"]),
  winning_direction: Direction.nullable(),
  btc_open: decimalString.nullable(),
  btc_close: decimalString.nullable(),
  btc_delta: decimalString.nullable(),
  order_id: z.string().nullable(),
  pnl_usdc: decimalString.nullable(),
  config_version_id: z.string(),
});
export type WindowSummary = z.infer<typeof WindowSummary>;

export const MinuteAggregate = z.object({
  // V3 multi-market: bucket count varies per market
  // (5m->5, 15m->15, 1h->12, 4h->24). Upper bound matches the
  // trading-bot's 4h/60s = 240 absolute cap.
  minute_index: z.number().int().min(1).max(240),
  btc_open: decimalString,
  btc_high: decimalString,
  btc_low: decimalString,
  btc_close: decimalString,
  up_high_bid: oddsString.nullable(),
  up_low_bid: oddsString.nullable(),
  down_high_bid: oddsString.nullable(),
  down_low_bid: oddsString.nullable(),
});
export type MinuteAggregate = z.infer<typeof MinuteAggregate>;

export const WindowDetail = WindowSummary.extend({
  minutes: z.array(MinuteAggregate),
  sample_count: z.number().int().nonnegative(),
});
export type WindowDetail = z.infer<typeof WindowDetail>;

export const WindowTick = z.object({
  ts: isoTimestamp,
  secs_left: z.number().int().nonnegative(),
  btc_price: decimalString,
  up_bid: oddsString.nullable(),
  up_ask: oddsString.nullable(),
  down_bid: oddsString.nullable(),
  down_ask: oddsString.nullable(),
  feed_health: FeedHealth,
});
export type WindowTick = z.infer<typeof WindowTick>;

export const DecisionCheckRow = z.object({
  check_ts: isoTimestamp,
  outcome: z.enum(["place", "skip", "blocked", "monitor", "already_placed"]),
  direction: Direction.nullable(),
  favorite_odds: oddsString.nullable(),
  matching_candles: z.number().int().nullable(),
  candles_evaluated: z.number().int().nullable(),
  block_reason: z.string().nullable(),
  checks: z.array(z.unknown()),
  config_version_id: z.string(),
});
export type DecisionCheckRow = z.infer<typeof DecisionCheckRow>;

export const WindowPage = z.object({ items: z.array(WindowSummary), page: Cursor });
export const TicksResp = z.object({ slug: z.string(), items: z.array(WindowTick) });
export const ChecksResp = z.object({ slug: z.string(), items: z.array(DecisionCheckRow) });

export const OrderSummary = z.object({
  order_id: z.string(),
  slug: z.string(),
  direction: Direction,
  mode: Mode,
  status: z.enum(["open", "filled", "rejected", "resolved", "failed"]),
  price: decimalString.nullable(),
  size_shares: decimalString.nullable(),
  size_usdc: decimalString.nullable(),
  fee_usdc: decimalString.nullable(),
  pnl_usdc: decimalString.nullable(),
  created_at: isoTimestamp,
  resolved_at: isoTimestamp.nullable(),
  config_version_id: z.string(),
  is_manual_buy: z.boolean().optional(),
  manual_sell: z.boolean().optional(),
  sold_early: z.boolean().optional(),
});
export type OrderSummary = z.infer<typeof OrderSummary>;

export const OrderEventRow = z.object({
  ts: isoTimestamp,
  phase: z.string(),
  price: decimalString.nullable(),
  filled_shares: decimalString.nullable(),
  fee_usdc: decimalString.nullable(),
  pnl_usdc: decimalString.nullable(),
  error: z.object({
    code: z.string(), message: z.string(), retryable: z.boolean().optional(),
  }).nullable(),
});

export const OrderDetail = OrderSummary.extend({ events: z.array(OrderEventRow) });
export type OrderDetail = z.infer<typeof OrderDetail>;
export const OrderPage = z.object({ items: z.array(OrderSummary), page: Cursor });

export const RunningStats = z.object({
  mode: Mode,
  from: isoTimestamp.nullable(),
  to: isoTimestamp.nullable(),
  total_orders: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  win_rate: decimalString,
  pnl_usdc: decimalString,
  fees_usdc: decimalString,
  average_odds: decimalString.nullable(),
});
export type RunningStats = z.infer<typeof RunningStats>;

export const AnalyticsSummary = z.object({
  mode: z.enum(["live", "paper", "all"]),
  from: isoTimestamp.nullable(),
  to: isoTimestamp.nullable(),
  total_windows: z.number().int().nonnegative(),
  running: z.object({
    trades: z.number().int().nonnegative(),
    wins: z.number().int().nonnegative(),
    losses: z.number().int().nonnegative(),
    win_rate: z.number().min(0),
    total_won_usdc: z.number(),
    total_lost_usdc: z.number(),
    net_pnl_usdc: z.number(),
    current_win_streak: z.number().int().nonnegative(),
    avg_wins_before_loss: z.number().min(0),
  }),
  directional: z.object({
    avg_up_fill_price: z.number().min(0),
    avg_down_fill_price: z.number().min(0),
    up_wins: z.number().int().nonnegative(),
    up_losses: z.number().int().nonnegative(),
    down_wins: z.number().int().nonnegative(),
    down_losses: z.number().int().nonnegative(),
  }),
  /**
   * Per-trade realized P&L series (one entry per resolved trade in the
   * filtered range), ordered by window start. The dashboard buckets these
   * into day-by-day and week-by-week (Sunday–Saturday) breakdowns in the
   * operator's local timezone.
   */
  pnl_series: z.array(z.object({
    ts: isoTimestamp,
    net_pnl_usdc: z.number(),
  })),
  kronos: z.object({
    enabled_trades: z.number().int().nonnegative(),
    enabled_wins: z.number().int().nonnegative(),
    enabled_losses: z.number().int().nonnegative(),
    enabled_win_rate: z.number().min(0),
    enabled_net_pnl_usdc: z.number(),
    disabled_trades: z.number().int().nonnegative(),
    disabled_wins: z.number().int().nonnegative(),
    disabled_losses: z.number().int().nonnegative(),
    disabled_win_rate: z.number().min(0),
    disabled_net_pnl_usdc: z.number(),
    by_market: z.array(z.object({
      market_id: z.string(),
      wins: z.number().int().nonnegative(),
      losses: z.number().int().nonnegative(),
      net_pnl_usdc: z.number(),
    })),
  }),
  /**
   * Per-market trading-stat breakdown across the filtered range, each with its
   * BTC<->ETH counterpart id. Powers the "By Market & Counterpart" table on the
   * All Markets analytics page.
   */
  by_market: z.array(z.object({
    market_id: z.string(),
    counterpart_market_id: z.string().nullable(),
    trades: z.number().int().nonnegative(),
    wins: z.number().int().nonnegative(),
    losses: z.number().int().nonnegative(),
    win_rate: z.number().min(0),
    net_pnl_usdc: z.number(),
    total_won_usdc: z.number(),
    total_lost_usdc: z.number(),
    up_wins: z.number().int().nonnegative(),
    up_losses: z.number().int().nonnegative(),
    down_wins: z.number().int().nonnegative(),
    down_losses: z.number().int().nonnegative(),
  })),
  /**
   * Counterpart-relationship breakdown from the primary asset's perspective
   * (All Markets => BTC vs ETH; per-market => that market vs its counterpart).
   * Win/loss is pnl>0. See data-api schema for cohort semantics.
   */
  counterpart: z.object({
    primary_asset: z.string(),
    counterpart_asset: z.string(),
    not_traded: z.array(z.object({
      timeframe: z.string(),
      counterpart_close: z.enum(["same", "opposite", "unresolved"]),
      won: z.number().int().nonnegative(),
      lost: z.number().int().nonnegative(),
    })),
    both_traded: z.array(z.object({
      timeframe: z.string(),
      direction: z.enum(["same", "different"]),
      both_win: z.number().int().nonnegative(),
      primary_win_cp_loss: z.number().int().nonnegative(),
      primary_loss_cp_win: z.number().int().nonnegative(),
      both_loss: z.number().int().nonnegative(),
    })),
  }),
});
export type AnalyticsSummary = z.infer<typeof AnalyticsSummary>;

export const SimulationRunRequest = z.object({
  mode: z.enum(["single", "sweep"]),
  config_version_id: z.string().nullable().optional(),
  config_override: z.record(z.unknown()).nullable().optional(),
  sweep: z.record(z.unknown()).nullable().optional(),
  target: z.enum(["auto", "bullmq", "modal"]).optional(),
  modal_batches: z.number().int().min(1).max(10_000).optional(),
  modal_artifacts_dir: z.string().min(1).optional(),
  modal_resume_only: z.boolean().optional(),
  modal_resume_from_run_id: z.string().optional(),
  from: isoTimestamp.nullable().optional(),
  to: isoTimestamp.nullable().optional(),
  subtract_no_fills_from_results: z.boolean().optional(),
  exclude_stale_odds: z.boolean().optional(),
  /**
   * V3 multi-market: restrict the simulation dataset to one market.
   * Omitting falls back to btc-5m on the data-api (V2 default).
   */
  market_id: z.string().regex(/^[a-z][a-z0-9-]*$/).optional(),
  /**
   * Batched sweeps (local bullmq target only). > 0 splits the combo
   * space into sequential slices of this many combinations each. 0 /
   * omitted = run the whole sweep in one job.
   */
  batch_size: z.number().int().min(0).max(1_000_000_000).optional(),
  /** Batched sweeps: ISO instant to defer the start until. Null = now. */
  scheduled_start_at: isoTimestamp.nullable().optional(),
  /**
   * Resource governor for local (bullmq) sweeps: cap parallelism to this
   * % of CPU cores / % of physical RAM. Soft caps (reduce worker count,
   * never hard-kill); ignored for Modal. Omitted = use all cores.
   */
  cpu_percent: z.number().min(1).max(100).optional(),
  ram_percent: z.number().min(1).max(100).optional(),
});
export type SimulationRunRequest = z.infer<typeof SimulationRunRequest>;

export const SimulationSavedQuery = z.object({
  mode: z.enum(["single", "sweep"]).optional(),
  target: z.enum(["auto", "bullmq", "modal"]).optional(),
});
export type SimulationSavedQuery = z.infer<typeof SimulationSavedQuery>;

export const ModalReadiness = z.object({
  ready: z.boolean(),
  command: z.string(),
  checks: z.object({
    modal_bin_found: z.boolean(),
    runner_exists: z.boolean(),
    token_ok: z.boolean(),
  }),
  errors: z.array(z.string()),
});
export type ModalReadiness = z.infer<typeof ModalReadiness>;

/** GET /v2/simulations/gate-timings — measured per-feature simulation cost. */
const GateCostEstimate = z.object({
  calls: z.number(),
  seconds: z.number(),
  ticks: z.number(),
  sec_per_ktick: z.number().nullable(),
  share_of_total: z.number().nullable(),
});
export const SimulationGateTimings = z.object({
  runs: z.number().int(),
  combos: z.number().int(),
  sampled_combos: z.number().int(),
  ticks: z.number().int(),
  min_combos_for_measured: z.number().int(),
  baseline_sec_per_ktick: z.number().nullable(),
  engines: z.record(GateCostEstimate),
  gates: z.record(GateCostEstimate),
  features: z.record(z.object({
    runs: z.number().int(),
    combos_on: z.number().int(),
    combos_off: z.number().int(),
    sec_per_ktick_on: z.number().nullable(),
    sec_per_ktick_off: z.number().nullable(),
    marginal_ratio: z.number().nullable(),
    gate_share: z.number().nullable(),
    grade: z.number().int().min(1).max(5).nullable(),
    measured: z.boolean(),
    basis: z.enum(["marginal", "gate_share"]).nullable(),
  })),
});
export type SimulationGateTimings = z.infer<typeof SimulationGateTimings>;

export const ModalResumeRequest = z.object({
  mode: z.literal("sweep").default("sweep"),
  target: z.literal("modal").default("modal"),
  config_version_id: z.string().nullable().optional(),
  sweep: z.record(z.unknown()).nullable(),
  modal_artifacts_dir: z.string().min(1),
  modal_resume_only: z.literal(true).default(true),
  modal_resume_from_run_id: z.string().optional(),
  from: isoTimestamp.nullable().optional(),
  to: isoTimestamp.nullable().optional(),
  subtract_no_fills_from_results: z.boolean().optional(),
  exclude_stale_odds: z.boolean().optional(),
});
export type ModalResumeRequest = z.infer<typeof ModalResumeRequest>;

export const SimulationRun = z.object({
  run_id: z.string(),
  status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
  mode: z.enum(["single", "sweep"]),
  request: z.record(z.unknown()).nullable().optional(),
  progress: z.number().min(0).max(1).nullable().optional(),
  created_at: isoTimestamp,
  started_at: isoTimestamp.nullable(),
  finished_at: isoTimestamp.nullable(),
  error: z.string().nullable(),
  result_summary: z.record(z.unknown()).nullable().optional(),
  saved: z.boolean().default(false),
  // 0009_simulation_batches: batch/schedule orchestration surface.
  is_batch_parent: z.boolean().optional(),
  paused: z.boolean().optional(),
  scheduled_start_at: isoTimestamp.nullable().optional(),
  batch_size: z.number().int().nullable().optional(),
  batch_total: z.number().int().nullable().optional(),
  batch_completed: z.number().int().nullable().optional(),
  parent_run_id: z.string().nullable().optional(),
});
export type SimulationRun = z.infer<typeof SimulationRun>;

export const SimulationDeleteResp = z.object({
  run_id: z.string(),
  deleted: z.boolean(),
});
export type SimulationDeleteResp = z.infer<typeof SimulationDeleteResp>;

export const SimCheckTraceRow = z.object({
  check_ts: z.string(),
  secs_left: z.number(),
  outcome: z.enum([
    "place",
    "skip",
    "blocked",
    "monitor",
    "already_placed",
    "take_profit",
    "stop_loss",
    "momentum_sell",
    "skip_after_wins",
  ]),
  direction: Direction.nullable(),
  favorite_odds: z.string().nullable(),
  matching_candles: z.number().int().nullable(),
  candles_evaluated: z.number().int().nullable(),
  block_reason: z.string().nullable(),
  checks: z.array(z.unknown()),
});
export type SimCheckTraceRow = z.infer<typeof SimCheckTraceRow>;

export const SimulationOutcomeTrace = z.object({
  run_id: z.string(),
  slug: z.string(),
  window_start: z.string(),
  decision_outcome: z.string(),
  direction: Direction.nullable(),
  favorite_odds: z.string().nullable(),
  result: z.string(),
  pnl_usdc: z.number(),
  trade_size_usdc: z.number(),
  check_trace: z.array(SimCheckTraceRow),
});
export type SimulationOutcomeTrace = z.infer<typeof SimulationOutcomeTrace>;

/** Funnel attribution shipped inside a single run's result_summary.gate_impact. */
export const SimulationGateImpactRow = z.object({
  feature: z.string(),
  kind: z.enum(["gate", "override"]),
  windows: z.number().int(),
  windows_any_tick: z.number().int(),
  wins: z.number().int(),
  losses: z.number().int(),
  unresolved: z.number().int(),
  pnl_usdc: z.string(),
});
export type SimulationGateImpactRow = z.infer<typeof SimulationGateImpactRow>;
export const SimulationGateImpact = z.object({
  attribution: z.literal("last_blocker"),
  gate_pnl_is_estimate: z.literal(true),
  rows: z.array(SimulationGateImpactRow),
});
export type SimulationGateImpact = z.infer<typeof SimulationGateImpact>;

/**
 * POST /v2/simulations/:run_id/gate-ablation?mode= — exact per-feature impact.
 * leave_one_out: full config minus one feature vs the full run.
 * standalone: core config plus one feature vs the core-only run.
 */
export const SimulationGateAblationMode = z.enum(["leave_one_out", "standalone"]);
export type SimulationGateAblationMode = z.infer<typeof SimulationGateAblationMode>;
const AblationAggregates = z.object({
  windows_with_bet: z.number().int(),
  wins: z.number().int(),
  losses_including_stop_losses: z.number().int(),
  pnl_usdc: z.string(),
});
export const SimulationGateAblation = z.object({
  run_id: z.string(),
  mode: SimulationGateAblationMode,
  baseline: AblationAggregates,
  baseline_source: z.enum(["saved", "ts_replay", "python_replay"]),
  engine: z.enum(["ts", "python"]),
  rows: z.array(z.object({
    feature: z.string(),
    with_feature: AblationAggregates,
    without: AblationAggregates,
    delta_orders: z.number().int(),
    delta_wins: z.number().int(),
    delta_losses: z.number().int(),
    delta_pnl_usdc: z.string(),
  })),
  elapsed_seconds: z.number(),
});
export type SimulationGateAblation = z.infer<typeof SimulationGateAblation>;

export const ConfigVersion = z.object({
  config_version_id: z.string(),
  created_at: isoTimestamp,
  created_by: z.string().nullable(),
  notes: z.string().nullable(),
  is_active: z.boolean(),
  applies_from_window_start: isoTimestamp.nullable(),
  config: z.record(z.unknown()),
});
export type ConfigVersion = z.infer<typeof ConfigVersion>;

export const ConfigVersionStats = z.object({
  config_version_id: z.string(),
  wins: z.number(),
  losses: z.number(),
  total_orders: z.number(),
  pnl_usdc: decimalString,
});
export type ConfigVersionStats = z.infer<typeof ConfigVersionStats>;

export const ConfigVersionStatsResponse = z.object({
  items: z.array(ConfigVersionStats),
});
export type ConfigVersionStatsResponse = z.infer<typeof ConfigVersionStatsResponse>;

export const ConfigActivation = z.object({
  config_version_id: z.string(),
  activated_at: isoTimestamp,
  applies_from_window_start: isoTimestamp,
});
export type ConfigActivation = z.infer<typeof ConfigActivation>;

export const ConfigPruneResult = z.object({
  active_config_version_id: z.string(),
  deleted_count: z.number().int().nonnegative(),
});
export type ConfigPruneResult = z.infer<typeof ConfigPruneResult>;

export const ConfigVersionPage = z.object({
  items: z.array(ConfigVersion),
  page: Cursor,
});
export type ConfigVersionPage = z.infer<typeof ConfigVersionPage>;

/**
 * V3 multi-market: response shape for `/v2/markets/:id/config/active`.
 *
 * `active` is null when the market has no per-market activation and no
 * eligible legacy fallback (i.e. any non-btc-5m market without its own
 * activation). `is_per_market` is true only when the response came
 * from `config_activation_per_market`; the btc-5m legacy fallback
 * reports false so the UI can label it as "global / legacy".
 */
export const MarketActiveConfig = z.object({
  market_id: z.string(),
  active: ConfigVersion.nullable(),
  is_per_market: z.boolean(),
});
export type MarketActiveConfig = z.infer<typeof MarketActiveConfig>;

/**
 * V3 multi-market: response for ``POST
 * /v2/markets/:market_id/config/activate/:cid``. Echoes the
 * activated ``market_id`` alongside the standard activation envelope.
 */
export const MarketConfigActivation = z.object({
  market_id: z.string(),
  config_version_id: z.string(),
  activated_at: isoTimestamp,
  applies_from_window_start: isoTimestamp,
});
export type MarketConfigActivation = z.infer<typeof MarketConfigActivation>;

// ----- Analytics page (mirrors V2 data-api /v2/analytics/*) ----------------

export const BetStatus = z.enum([
  "won", "lost", "no_fill", "skipped", "no_bet", "pending", "sold_early",
]);
export type BetStatus = z.infer<typeof BetStatus>;

export const AnalyticsWindowSummary = z.object({
  slug: z.string(),
  /** V3 multi-market: which market this row belongs to. Legacy NULL
   *  rows are normalised to "btc-5m" by the data-api so this field is
   *  always present. */
  market_id: z.string().regex(/^[a-z][a-z0-9-]*$/),
  window_start: isoTimestamp,
  window_end: isoTimestamp,
  status: z.enum(["open", "awaiting_resolution", "resolved"]),
  winning_direction: Direction.nullable(),
  btc_open: decimalString.nullable(),
  btc_close: decimalString.nullable(),
  btc_delta: decimalString.nullable(),
  bet_status: BetStatus,
  bet_mode: Mode.nullable(),
  bet_direction: Direction.nullable(),
  pnl_usdc: decimalString.nullable(),
  size_usdc: decimalString.nullable(),
  hidden: z.boolean(),
  sold_early: z.boolean(),
  exit_reason: z.enum(["take_profit", "stop_loss", "manual", "momentum_sell"]).nullable(),
  is_manual_buy: z.boolean(),
  config_version_id: z.string(),
  order_id: z.string().nullable(),
  // Counterpart (BTC<->ETH, same timeframe + timestamp) surface. Populated on
  // the window list so a traded window can show whether the opposite-asset
  // market was also traded (win/lose) and which direction it closed. Defaulted
  // to null so responses that predate this field (or the detail endpoint)
  // still parse.
  counterpart_slug: z.string().nullable(),
  counterpart_market_id: z.string().nullable(),
  counterpart_winning_direction: Direction.nullable(),
  counterpart_bet_status: BetStatus.nullable(),
  counterpart_direction: Direction.nullable(),
  counterpart_override_active: z.boolean().optional(),
  /** Set when the order was entered via the Favorite Hold feature
   *  ("tier1" | "tier2" | "tier3"). Inherited by AnalyticsWindowDetail. */
  favorite_hold_tier: z
    .union([z.literal("tier1"), z.literal("tier2"), z.literal("tier3")])
    .nullable()
    .optional(),
});
export type AnalyticsWindowSummary = z.infer<typeof AnalyticsWindowSummary>;

export const AnalyticsKpis = z.object({
  won: z.number().int().nonnegative(),
  lost: z.number().int().nonnegative(),
  no_fill: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  no_bet: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  sold_early: z.number().int().nonnegative(),
  stop_loss: z.number().int().nonnegative(),
  take_profit: z.number().int().nonnegative(),
  hidden: z.number().int().nonnegative(),
  manual_buy: z.number().int().nonnegative(),
});
export type AnalyticsKpis = z.infer<typeof AnalyticsKpis>;

export const AnalyticsWindowPage = z.object({
  items: z.array(AnalyticsWindowSummary),
  page: Cursor,
  kpis: AnalyticsKpis,
});
export type AnalyticsWindowPage = z.infer<typeof AnalyticsWindowPage>;

export const ProbabilityTiming = z.object({
  side: Direction,
  first_min_offset: z.number().int().nonnegative().nullable(),
  last_min_offset:  z.number().int().nonnegative().nullable(),
  first_max_offset: z.number().int().nonnegative().nullable(),
  last_max_offset:  z.number().int().nonnegative().nullable(),
  first_stop_loss_offset: z.number().int().nonnegative().nullable(),
  last_stop_loss_offset:  z.number().int().nonnegative().nullable(),
  first_below_50_offset:  z.number().int().nonnegative().nullable(),
  last_below_50_offset:   z.number().int().nonnegative().nullable(),
  min_prob_observed:      oddsString.nullable(),
});
export type ProbabilityTiming = z.infer<typeof ProbabilityTiming>;

export const AnalyticsBet = z.object({
  order_id: z.string(),
  exchange_order_id: z.string().nullable(),
  direction: Direction,
  mode: Mode,
  status: z.enum(["open", "filled", "rejected", "resolved", "failed"]),
  bet_status: BetStatus,
  price: decimalString.nullable(),
  size_usdc: decimalString.nullable(),
  size_shares: decimalString.nullable(),
  filled_shares: decimalString.nullable(),
  fee_usdc: decimalString.nullable(),
  pnl_usdc: decimalString.nullable(),
  created_at: isoTimestamp,
  resolved_at: isoTimestamp.nullable(),
  hidden: z.boolean(),
  sold_early: z.boolean(),
  skip_due_to_win_streak: z.boolean(),
  is_manual_buy: z.boolean(),
  manual_sell: z.boolean(),
  exit_reason: z.enum(["take_profit", "stop_loss", "manual", "momentum_sell"]).nullable(),
  pre_order_metrics: z.object({
    sample_count: z.number().int().nonnegative(),
    avg_mid: z.number().nullable().optional(),
    avg_up_mid: z.number().nullable().optional(),
    avg_down_mid: z.number().nullable().optional(),
    up_min_mid: z.number().nullable().optional(),
    up_max_mid: z.number().nullable().optional(),
    down_min_mid: z.number().nullable().optional(),
    down_max_mid: z.number().nullable().optional(),
    btc_open: z.number().nullable().optional(),
    btc_min_pct: z.number().nullable().optional(),
    btc_max_pct: z.number().nullable().optional(),
  }).passthrough().nullable().optional(),
});
export type AnalyticsBet = z.infer<typeof AnalyticsBet>;

export const AnalyticsWindowDetail = AnalyticsWindowSummary.extend({
  sample_count: z.number().int().nonnegative(),
  feed_source: z.string().nullable(),
  outcome_source: z.string().nullable(),
  resolved_at: isoTimestamp.nullable(),
  no_fill_attempt_timestamps: z.array(isoTimestamp),
  /** V3 multi-market: total window length in seconds (5m=300,
   *  15m=900, 1h=3600, 4h=14400). Always populated by data-api. */
  window_seconds: z.number().int().positive(),
  /** V3 multi-market: per-bucket span in seconds. Bucket count =
   *  window_seconds / bucket_seconds. Always populated. */
  bucket_seconds: z.number().int().positive(),
  /** V3 multi-market: true when this window's market has no per-market
   *  config activation. Hide trading-related UI (config row, decision
   *  checks) — the stored config_version_id is just a NOT-NULL filler
   *  and doesn't reflect what the bot was actually doing. */
  is_data_only: z.boolean(),
  minutes: z.array(MinuteAggregate),
  probability_timing: z.array(ProbabilityTiming),
  pre_order_metrics_window: z.object({
    sample_count: z.number().int().nonnegative(),
    avg_mid: z.number().nullable().optional(),
    avg_up_mid: z.number().nullable().optional(),
    avg_down_mid: z.number().nullable().optional(),
    up_min_mid: z.number().nullable().optional(),
    up_max_mid: z.number().nullable().optional(),
    down_min_mid: z.number().nullable().optional(),
    down_max_mid: z.number().nullable().optional(),
    btc_open: z.number().nullable().optional(),
    btc_min_pct: z.number().nullable().optional(),
    btc_max_pct: z.number().nullable().optional(),
  }).passthrough().nullable().optional(),
  bet: AnalyticsBet.nullable(),
});
export type AnalyticsWindowDetail = z.infer<typeof AnalyticsWindowDetail>;

export const OutcomeProposal = z.object({
  slug: z.string(),
  changed: z.boolean(),
  current: z.object({
    winning_direction: Direction.nullable(),
    won: z.boolean().nullable(),
    net_pnl_usdc: decimalString.nullable(),
    outcome_source: z.string().nullable(),
    btc_start: decimalString.nullable().optional(),
    btc_end: decimalString.nullable().optional(),
    btc_delta: decimalString.nullable().optional(),
  }),
  proposed: z.object({
    winning_direction: Direction.nullable(),
    won: z.boolean().nullable(),
    net_pnl_usdc: decimalString.nullable(),
    outcome_source: z.string(),
    btc_start: decimalString.nullable().optional(),
    btc_end: decimalString.nullable().optional(),
    btc_delta: decimalString.nullable().optional(),
  }),
  window_only: z.boolean(),
  proposed_order: z.object({
    direction: Direction,
    filled_shares: decimalString,
    size_usdc: decimalString,
    avg_price: decimalString,
    fee_usdc: decimalString.nullable().optional(),
    pnl_usdc: decimalString.nullable(),
    mode: z.enum(["live", "paper"]),
    trade_count: z.number().int().nonnegative(),
    source: z.literal("polymarket_trades"),
    has_realized_exits: z.boolean(),
  }).nullable().optional(),
  early_exit: z.object({
    exit_at: z.string(),
    exit_reason: z.enum(["take_profit", "stop_loss", "manual"]),
    sell_shares: decimalString.nullable().optional(),
    sell_price: decimalString.nullable().optional(),
    sell_usdc: decimalString.nullable().optional(),
    pnl_usdc: decimalString.nullable().optional(),
  }).nullable().optional(),
});
export type OutcomeProposal = z.infer<typeof OutcomeProposal>;

export const OkResp = z.object({ ok: z.literal(true) }).passthrough();

/** `POST /v2/orders/:order_id/visibility` — backend echoes the new state. */
export const VisibilityResp = z.object({
  order_id: z.string(),
  hidden: z.boolean(),
});
export type VisibilityResp = z.infer<typeof VisibilityResp>;

/** `POST /v2/analytics/windows/:slug/visibility` — backend echoes the new state. */
export const WindowVisibilityResp = z.object({
  slug: z.string(),
  hidden: z.boolean(),
});
export type WindowVisibilityResp = z.infer<typeof WindowVisibilityResp>;

export const ManualCommandResult = z.object({
  ok: z.boolean(),
  message: z.string(),
  order_id: z.string().optional(),
  price: decimalString.optional(),
  pnl_usdc: decimalString.optional(),
}).passthrough();
export type ManualCommandResult = z.infer<typeof ManualCommandResult>;

/**
 * 0010_grafana_dashboards: server-side registry of Grafana dashboards shown
 * under the "Grafana" sidebar section and edited on the Settings page.
 */
export const GrafanaDashboardDto = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string(),
});
export type GrafanaDashboardDto = z.infer<typeof GrafanaDashboardDto>;

export const GrafanaDashboardListResp = z.array(GrafanaDashboardDto);

/**
 * Live/analytics DB split: `GET /v2/sync/status` — powers the header
 * "Sync Data" button. `mode === "single"` means the split is disabled
 * (analytics == live) and the button hides itself.
 */
export const SyncStatus = z.object({
  mode: z.enum(["split", "single"]),
  state: z.enum(["disabled", "initial_build", "idle", "syncing", "error"]),
  live_db_path: z.string(),
  analytics_db_path: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  last_duration_ms: z.number().nullable(),
  last_rows_copied: z.number().nullable(),
  last_rows_deleted: z.number().nullable(),
  last_error: z.string().nullable(),
  syncs_completed: z.number(),
  initial_build_bytes: z.number().nullable(),
});
export type SyncStatus = z.infer<typeof SyncStatus>;

/** `POST /v2/sync` — resolves AFTER the incremental copy completes. */
export const SyncRunResp = z.object({
  ok: z.literal(true),
  duration_ms: z.number(),
  rows_copied: z.number(),
  rows_deleted: z.number(),
  migrations_applied: z.array(z.string()),
  status: SyncStatus,
});
export type SyncRunResp = z.infer<typeof SyncRunResp>;
